import * as THREE from 'three';
import { findClosestPointOnPoints } from '../utils';

/**
 * Creates a closed surface geometry from two curves.
 * 
 * @param {Array} points0 - The points of the original curve.
 * @param {Array} points1 - The points of the offset curve.
 * @returns {THREE.BufferGeometry} The closed surface geometry.
 */
export const closedSurfaceGeometryfromTwoCurves = (points0, points1) => {
  const geometry = new THREE.BufferGeometry();
  // Array to store the position information of vertices
  const vertices = [];
  const indices = [];

  // Calculate the points of the original curve and the offset curve, and add them to the vertices array.
  points0.forEach((point, i) => {
    vertices.push(point.x, point.y, point.z);
    
    const offsetPoint = points1[i];
    vertices.push(offsetPoint.x, offsetPoint.y, offsetPoint.z);

    // Add indices (elements).
    // Since it is a closed curve, the last point is connected to the first point.
    const nextIndex = (i + 1) % points0.length;
    indices.push(i * 2, i * 2 + 1, nextIndex * 2);
    indices.push(i * 2 + 1, nextIndex * 2 + 1, nextIndex * 2);
  });  

  // Add vertex position information to BufferGeometry.
  const verticesArray = new Float32Array(vertices);
  const positionAttribute = new THREE.BufferAttribute(verticesArray, 3);
  geometry.setAttribute('position', positionAttribute);

  // Add index information to BufferGeometry.
  const indicesArray = new Uint16Array(indices);
  geometry.setIndex(new THREE.BufferAttribute(indicesArray, 1));

  // Compute vertex normals.
  geometry.computeVertexNormals();

  return geometry;
}

// good only for simple curve
/**
 * Creates a closed surface geometry from an array of points.
 * 
 * @param {THREE.Vector3[]} points - The array of points defining the shape.
 * @returns {THREE.BufferGeometry} The generated closed surface geometry.
 */
export const closedSurfaceGeometryfromCurve = (points) => {
  const geometry = new THREE.BufferGeometry();
  // Array to store the position information of vertices
  const vertices = [];
  const indices = [];

  // calc center point
  const center = new THREE.Vector3();
  points.forEach((point) => {
    center.add(point);
  });
  center.divideScalar(points.length);
  vertices.push(center.x, center.y, center.z);

  // Create a triangular mesh that includes the center and passes through each point in order.
  points.forEach((point, i) => {
    vertices.push(point.x, point.y, point.z);

    // next point
    const nextPoint = points[(i + 1) % points.length];
    vertices.push(nextPoint.x, nextPoint.y, nextPoint.z);

    // Add indices (elements).
    indices.push(0, i*2 + 1 , (i*2 + 2) );
  });


  // Add vertex position information to BufferGeometry.
  const verticesArray = new Float32Array(vertices);
  const positionAttribute = new THREE.BufferAttribute(verticesArray, 3);
  geometry.setAttribute('position', positionAttribute);

  // Add index information to BufferGeometry.
  const indicesArray = new Uint16Array(indices);
  geometry.setIndex(new THREE.BufferAttribute(indicesArray, 1));

  // Compute vertex normals.
  geometry.computeVertexNormals();

  return geometry;
}

/**
 * Creates a closed surface geometry from a curve using Delaunator triangulation.
 * 
 * @param {Array<Object>} points - The array of points representing the curve.
 * @returns {THREE.BufferGeometry} The generated closed surface geometry.
 */
export const closedSurfaceGeometryfromCurveWithDelaunator = (points) => {
  // Convert points to 2D array for Delaunator (ignoring the z-coordinate)
  const points2D = points.map(p => [p.x, p.y]);

  // Perform triangulation using Delaunator
  const delaunay = Delaunator.from(points2D);
  const triangles = delaunay.triangles;

  // Create BufferGeometry
  const geometry = new THREE.BufferGeometry();
  const vertices = [];

  // Create vertex array using the indices returned by Delaunator
  triangles.forEach(i => {
    vertices.push(points[i].x, points[i].y, points[i].z);
  });

  // Set vertex position information to BufferGeometry
  const verticesArray = new Float32Array(vertices);
  geometry.setAttribute('position', new THREE.BufferAttribute(verticesArray, 3));

  // Compute vertex normals
  geometry.computeVertexNormals();

  return geometry;
}

/**
 * Creates a side surface geometry from two curves.
 *
 * @param {THREE.Vector3[]} points0 - The points of the original curve.
 * @param {THREE.Vector3[]} points1 - The points of the offset curve.
 * @returns {THREE.BufferGeometry} The generated side surface geometry.
 */
export const sideSurfaceGeometryfromTwoCurves = (points0, points1) => {
  const geometry = new THREE.BufferGeometry();
  // Array to store the position information of vertices
  const vertices = [];
  const indices = [];

  // Calculate the points of the original curve and the offset curve, and add them to the vertices array.
  points0.forEach((point, i) => {
    vertices.push(point.x, point.y, point.z);
    
    const offsetPoint = points1[i];
    vertices.push(offsetPoint.x, offsetPoint.y, offsetPoint.z);

    // Add indices (elements).
    // Since it is a closed curve, the last point is connected to the first point.
    const nextIndex = (i + 1) % points0.length;
    indices.push(i * 2, i * 2 + 1, nextIndex * 2);
    indices.push(i * 2 + 1, nextIndex * 2 + 1, nextIndex * 2);
  });

  // Add vertex position information to BufferGeometry.
  const verticesArray = new Float32Array(vertices);
  const positionAttribute = new THREE.BufferAttribute(verticesArray, 3);
  geometry.setAttribute('position', positionAttribute);

  // Add index information to BufferGeometry.
  const indicesArray = new Uint16Array(indices);
  geometry.setIndex(new THREE.BufferAttribute(indicesArray, 1));

  // Compute vertex normals.
  geometry.computeVertexNormals();

  return geometry;
}

/**
 * Creates a surface geometry from inward and outward points.
 * 
 * @param {THREE.Vector3[]} inwardPoints - The inward points of the surface.
 * @param {THREE.Vector3[]} outwardPoints - The outward points of the surface.
 * @returns {THREE.BufferGeometry} - The surface geometry.
 */
export const surfaceFromInwardOutwardPoints = (inwardPoints, outwardPoints) => {
  let vertices = [];
  for (let i = 0; i < inwardPoints.length-1; i++) {
    // vertices.push(inwardPoints[i], inwardPoints[i+1], outwardPoints[i]);
    // vertices.push(outwardPoints[i], outwardPoints[i+1], inwardPoints[i+1]);
    vertices.push(inwardPoints[i], outwardPoints[i], inwardPoints[i+1]);
    vertices.push(outwardPoints[i], inwardPoints[i+1], outwardPoints[i+1]);
  }
  // last triangle
  vertices.push(inwardPoints[inwardPoints.length-1], inwardPoints[0], outwardPoints[inwardPoints.length-1]);

  // convert from Vecor3Array to Float32Array
  vertices = vertices.map((v) => {
    return [v.x, v.y, v.z];
  });
  vertices = vertices.flat();
  vertices = new Float32Array(vertices);

  const surfaceGeometry = new THREE.BufferGeometry()
  // // This is non-index format
  // surfaceGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  
  // This is indexed format
  surfaceGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  const indices = [];
  for (let i = 0; i < vertices.length/3; i++) {
    indices.push(i);
  }
  surfaceGeometry.setIndex(indices);
  surfaceGeometry.computeVertexNormals();

  return surfaceGeometry;
}

export const splitSurface = (targetSurfaceGeometry, trimSurfaceGeometry) => {
  // judege index or non-index
  if (targetSurfaceGeometry.index === null) {
    console.log("Target surface is non-indexed.");
    // TODO: add convert function from non-index to index
  } else {
    // console.log("Target surface is indexed.");
  }

  if (trimSurfaceGeometry.index === null) {
    console.log("Trim surface is non-indexed.");
    // TODO: add convert function from non-index to index
  } else {
    // console.log("Trim surface is indexed.");
  }

  let cnt = 0
  let isFlip = false;
  let newGeometry = targetSurfaceGeometry.clone();

  trimSurfaceGeometry.computeVertexNormals();
  const trimSurfaceNormal = trimSurfaceGeometry.attributes.normal.array;
  const trimSurfacePosition = trimSurfaceGeometry.attributes.position.array;
  // convert trimSurfacePosition from Float32Array to Array of Vector3
  const trimSurfacePositionArray = [];
  for (let i = 0; i < trimSurfacePosition.length; i+=3) {
    trimSurfacePositionArray.push(new THREE.Vector3(trimSurfacePosition[i], trimSurfacePosition[i+1], trimSurfacePosition[i+2]));
  }

  // convert trimSurfaceGeometry to object3d
  const trimSurfaceObject = new THREE.Mesh(trimSurfaceGeometry) //new THREE.Object3D();
  // set doubleside https://threejs.org/docs/#api/en/core/Raycaster.intersectObject
  // Note that for meshes, faces must be pointed towards the origin of the ray in order to be detected; 
  // intersections of the ray passing through the back of a face will not be detected. 
  // To raycast against both faces of an object, you'll want to set the material's side property to THREE.DoubleSide.
  trimSurfaceObject.material.side = THREE.DoubleSide;

  const intersections = [];
  const intersectionFaceIndices = [];
  const intersectionFaceVertexIndices = [];
  const raycaster = new THREE.Raycaster();
  
  // get target surface vertices
  const vertices = targetSurfaceGeometry.attributes.position.array;
  const faces = targetSurfaceGeometry.index.array;
  const targetNormal = targetSurfaceGeometry.attributes.normal.array;

  const newVertices = [];
  const newIndices = [];
  // count the number of intersection points for each triangle
  const intersectionCount = new Array(faces.length/3).fill(0);
  const removeFaceIndices = [];

  // perform a ray cast for each side of the triangle
  for (let i = 0; i < faces.length; i += 3) {
    const tempIntersections = [];
    const tempIndices = [];
    const tempIntersectionFaceVertexIndices = [];

    const a = new THREE.Vector3(vertices[faces[i] * 3], vertices[faces[i] * 3 + 1], vertices[faces[i] * 3 + 2]);
    const b = new THREE.Vector3(vertices[faces[i + 1] * 3], vertices[faces[i + 1] * 3 + 1], vertices[faces[i + 1] * 3 + 2]);
    const c = new THREE.Vector3(vertices[faces[i + 2] * 3], vertices[faces[i + 2] * 3 + 1], vertices[faces[i + 2] * 3 + 2]);

    // perform a ray cast for each side of the triangle
    [a, b, c].forEach((vertex, index) => {
      const nextIndex = index === 2 ? 0 : index + 1;
      const nextVertex = [a, b, c][nextIndex];

      const vertexIndex = faces[[i, i+1, i+2][index]]
      const nextVertexIndex = faces[[i, i+1, i+2][nextIndex]]
      const key = [vertexIndex, nextVertexIndex].sort().join('-')

      // calculate direction of the ray
      const direction = nextVertex.clone().sub(vertex).normalize();
      // raycaster setting
      raycaster.set(vertex, direction);
      const rayLength = vertex.distanceTo(nextVertex);
      raycaster.far = rayLength;
      
      // get intersection points
      const intersects = raycaster.intersectObject(trimSurfaceObject);

      if (intersects.length > 0 && intersects[0].distance < rayLength) {
        intersections.push(intersects[0].point);
        intersectionFaceIndices.push(intersects[0].faceIndex); // trim surface face index
        intersectionFaceVertexIndices.push([intersects[0].face.a, intersects[0].face.b, intersects[0].face.c]); // trim surface vertex index
        
        // this is for split
        tempIntersections.push(intersects[0]);
        tempIndices.push(index);
        tempIntersectionFaceVertexIndices.push([intersects[0].face.a, intersects[0].face.b, intersects[0].face.c]);
      }
    });

    // split triangle with intersection points
    if (tempIntersections.length == 2) {
      removeFaceIndices.push(i/3);
      let newTriangle = [];
      let newTriangle1 = [];
      let newTriangle2 = [];

      // detect isolated vertex (there are three vertices in a triangle, and I want to know which vertex is triangle with isolated edge)
      let isolatedIndex = 0
      const sumIndex = tempIndices[0] + tempIndices[1];
      if (sumIndex == 1) {
        isolatedIndex = 1;
      } else if (sumIndex == 2) {
        isolatedIndex = 0;
      } else {
        isolatedIndex = 2;
      }

      // calc average vertex normal of target surface face
      const targetAverageNormalA = new THREE.Vector3(targetNormal[faces[i]*3], targetNormal[faces[i]*3+1], targetNormal[faces[i]*3+2]);
      const targetAverageNormalB = new THREE.Vector3(targetNormal[faces[i+1]*3], targetNormal[faces[i+1]*3+1], targetNormal[faces[i+1]*3+2]);
      const targetAverageNormalC = new THREE.Vector3(targetNormal[faces[i+2]*3], targetNormal[faces[i+2]*3+1], targetNormal[faces[i+2]*3+2]);
      const targetAverageNormal = targetAverageNormalA.add(targetAverageNormalB).add(targetAverageNormalC).divideScalar(3);

      // calc average vertex normal of trim surface with two intersection points
      // average normal of tempIntersectionFaceVertexIndices
      const normals = tempIntersectionFaceVertexIndices.flat().map((vertexIndex) => {
        let normal = new THREE.Vector3(trimSurfaceNormal[vertexIndex*3], trimSurfaceNormal[vertexIndex*3+1], trimSurfaceNormal[vertexIndex*3+2]);
        if (normal.y < 0) {
          normal = normal.multiplyScalar(-1);
        }
        return normal;
      });
      // average normals
      const averageNormal = normals.reduce((a, b) => a.add(b)).divideScalar(normals.length);
      // calculate isolated vertex normal
      // find closest points of isolated vertex from trim surface vertices
      const isolatedVertex = [a, b, c][isolatedIndex];
      // find closest points of isolated vertex from trim surface vertices and get normal of the point
      const closestPoint = findClosestPointOnPoints(trimSurfacePositionArray, isolatedVertex);
      // calc vector from closest point to isolated vertex
      const closestPointNormal = isolatedVertex.clone().sub(closestPoint.point).normalize();
      // calc inner product of averageNormal and closestPointNormal
      const innerProduct = averageNormal.dot(closestPointNormal);

    
      // detect isolatedIndex y value is max or not
      const isolatedYs = [a.y, b.y, c.y];
      const maxIsolatedY = Math.max(...isolatedYs);
      const maxIsolatedYIndex = isolatedYs.indexOf(maxIsolatedY);
      const minIsolatedY = Math.min(...isolatedYs);
      const minIsolatedYIndex = isolatedYs.indexOf(minIsolatedY);


      // make triangles
      // make new triangle with two intersection points and one original point
      if (innerProduct < 0) {
      //if (isolatedIndex !== maxIsolatedYIndex && isolatedIndex == minIsolatedYIndex) {
      //if (isolatedIndex !== maxIsolatedYIndex) {
        newTriangle[tempIndices[0]] = tempIntersections[0].point;
        newTriangle[tempIndices[1]] = tempIntersections[1].point;
        newTriangle[3-tempIndices[0]-tempIndices[1]] = [a, b, c][isolatedIndex];

        // flip vertices if inner product is negative
        [newTriangle, isFlip] = flipVerticesBasedOnNormal(newTriangle, targetAverageNormal);

        // add new triangle to newVertices and newIndices
        newTriangle.forEach((point) => {
          newVertices.push(point.x, point.y, point.z);
        });
        newIndices.push(cnt, cnt+1, cnt+2);
        cnt += 3;
      } else {
        // If we split triangle with intersection points,
        // we have three original points and two intersection points.
        // So we can make new triangle with two intersection points and one original point.
        // And we have two remaining original points and two intersection point as rectangle.
        // So we divide rectangle to two triangles.

        // make new triangle with two intersection points and another original point
        if (isolatedIndex == 0) {
          newTriangle1[0] = tempIntersections[0].point;
          newTriangle1[1] = tempIntersections[1].point;
          newTriangle1[2] = [a, b, c][2];
        } else if (isolatedIndex == 1) {
          newTriangle1[0] = tempIntersections[0].point;
          newTriangle1[1] = tempIntersections[1].point;
          newTriangle1[2] = [a, b, c][0];
        } else {
          newTriangle1[0] = tempIntersections[0].point;
          newTriangle1[1] = tempIntersections[1].point;
          newTriangle1[2] = [a, b, c][1];
        }

        // flip vertices if inner product is negative
        [newTriangle1, isFlip] = flipVerticesBasedOnNormal(newTriangle1, targetAverageNormal);
        // add new triangle to newVertices and newIndices
        newTriangle1.forEach((point) => {
          newVertices.push(point.x, point.y, point.z);
        });
        newIndices.push(cnt, cnt+1, cnt+2);
        cnt += 3;


        const idxIntersection = (isFlip) ? 0 : 1;

        if (isolatedIndex == 0) {
          newTriangle2[0] = tempIntersections[idxIntersection].point;
          newTriangle2[1] = [a, b, c][1];
          newTriangle2[2] = [a, b, c][2];
        } else if (isolatedIndex == 1) {
          newTriangle2[0] = tempIntersections[idxIntersection].point;
          newTriangle2[1] = [a, b, c][2];
          newTriangle2[2] = [a, b, c][0];
        } else {
          newTriangle2[0] = tempIntersections[idxIntersection].point;
          newTriangle2[1] = [a, b, c][0];
          newTriangle2[2] = [a, b, c][1];
        }

        // flip vertices if inner product is negative
        [newTriangle2, isFlip] = flipVerticesBasedOnNormal(newTriangle2, targetAverageNormal);
        // add new triangle to newVertices and newIndices
        newTriangle2.forEach((point) => {
          newVertices.push(point.x, point.y, point.z);
        });
        newIndices.push(cnt, cnt+1, cnt+2);
        cnt += 3;
      }
    }
  }

  // remove triangles which has intersectionCount is 2
  const newVertices2 = [];
  const newIndices2 = [];

  // remove triangles which has intersectionCount is 2
  for (let i = 0; i < faces.length; i += 3) {
    if (!removeFaceIndices.includes(i/3)) {
      newIndices2.push(faces[i], faces[i+1], faces[i+2]);
    }
  }

  // add new vertices and indices to newGeometry, this is to show around the intersection points
  newGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
  newGeometry.setIndex(new THREE.BufferAttribute(new Uint16Array(newIndices2), 1));

  const splitGeometries = split_mesh_islands(newGeometry);
  // calculate bbox of each geometry and center of bbox
  const bboxs = [];
  const centers = [];
  splitGeometries.forEach((geometry) => {
    geometry.computeBoundingBox();
    bboxs.push(geometry.boundingBox);
    centers.push(geometry.boundingBox.getCenter(new THREE.Vector3()));
  });

  // find min y bbox center
  let minCenter = centers[0];
  let minCenterIndex = 0;
  for (let i = 0; i < centers.length; i++) {
    if (centers[i].y < minCenter.y) {
      minCenter = centers[i];
      minCenterIndex = i;
    }
  }

  const tempFinalGeometry = splitGeometries[minCenterIndex];
  if (tempFinalGeometry.length !== 2) {
    console.warn("finalGeometry.length !== 2", tempFinalGeometry.length);
  }

  // add new vertices and indices to tempFinalGeometry, this is to show triangles around the intersection points
  const tempFinalGeometryVertices = tempFinalGeometry.attributes.position.array; // non-index format
  const combinedVertices = new Float32Array(tempFinalGeometryVertices.length + newVertices.length);
  combinedVertices.set(tempFinalGeometryVertices);
  combinedVertices.set(newVertices, tempFinalGeometryVertices.length);
  
  const finalGeometry = new THREE.BufferGeometry();
  finalGeometry.setAttribute('position', new THREE.BufferAttribute(combinedVertices, 3));
  finalGeometry.computeVertexNormals();

  // // add new vertices and indices to newGeometry, this is to show around the intersection points
  // newGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(newVertices), 3));
  // newGeometry.setIndex(new THREE.BufferAttribute(new Uint16Array(newIndices), 1));

  return [intersections, finalGeometry, newVertices.length];
}


const flipVerticesBasedOnNormal = (vertices, refNormal) => {
  let isFlip = false;

  // convert array of Vector3 to Float32Array
  let verticesFloat32Array = vertices.map((point) => {
    return [point.x, point.y, point.z];
  });
  verticesFloat32Array = verticesFloat32Array.flat();
  verticesFloat32Array = new Float32Array(verticesFloat32Array);

  // calc newTriangle1 normal
  const newTriangle1Geometry = new THREE.BufferGeometry();
  newTriangle1Geometry.setAttribute('position', new THREE.BufferAttribute(verticesFloat32Array, 3));
  newTriangle1Geometry.setIndex(new THREE.BufferAttribute(new Uint16Array([0, 1, 2]), 1));
  newTriangle1Geometry.computeVertexNormals();
  const newTriangle1Normal = newTriangle1Geometry.attributes.normal.array;

  // calc inner product of newTriangle1Normal and refNormal
  const newTriangle1NormalVector = new THREE.Vector3(newTriangle1Normal[0], newTriangle1Normal[1], newTriangle1Normal[2]);
  const innerProduct = newTriangle1NormalVector.dot(refNormal);

  // flip vertices if inner product is negative
  if (innerProduct < 0) {
    isFlip = true;
    return [[vertices[0], vertices[2], vertices[1]], isFlip];
  }
  return [vertices, isFlip];
}

function find(parents, x) {
  if (typeof parents[x] != "undefined") {
    if (parents[x] < 0) {
      return x; //x is a parent
    } else {
      //recurse until you find x's parent
      return find(parents, parents[x]);
    }
  } else {
    // initialize this node to it's on parent (-1)
    parents[x] = -1;
    // console.log("init", x)
    return x; //return the index of the parent
  }
}

function union(parents, x, y) {
  var xpar = find(parents, x);
  var ypar = find(parents, y);
  if (xpar != ypar) {
    // x's parent is now the parent of y also.
    // if y was a parent to more than one node, then
    // all of those nodes are now also connected to x's parent.
    parents[xpar] += parents[ypar];
    parents[ypar] = xpar;
    return false;
  } else {
    return true; //this link creates a cycle
  }
}

function split_mesh_islands(check_geom) {
  let parents = [];
  let new_geoms = [];

  for (let i = 0; i < check_geom.index.array.length; i+=3) {
    let v_index = [check_geom.index.array[i],
             check_geom.index.array[i+1],
             check_geom.index.array[i+2]];
    let tri = new THREE.Triangle();
    tri.a.fromBufferAttribute(check_geom.attributes.position, v_index[0]);
    tri.b.fromBufferAttribute(check_geom.attributes.position, v_index[1]);
    tri.c.fromBufferAttribute(check_geom.attributes.position, v_index[2]);
    // make triangle
    // const triangle = new THREE.Triangle(tri.a, tri.b, tri.c);
    // if (triangle.isDegenerate()) {
    // 	console.warn("isTriDegenerate", i, tri);
    // 	continue;
    // }
    if (tri.a.equals(tri.b) || tri.b.equals(tri.c) || tri.c.equals(tri.a)) {
      console.warn("isTriDegenerate", i, tri);
      continue;
    }

    // console.log(i, tri.a, tri.b, tri.c);

    union(parents, check_geom.index.array[i], check_geom.index.array[i+1]);
    union(parents, check_geom.index.array[i+1], check_geom.index.array[i+2]);
    union(parents, check_geom.index.array[i], check_geom.index.array[i+2]);
  }

  for (let i = 0; i < parents.length; i++) {
    parents[i] = find(parents, parents[i]);
  }
  console.log("check_geom", check_geom)
  // console.log("parents", check_geom.index.array.length, parents)

  for (let i = 0; i < parents.length; i++) {
    if (parents[i] < 0) {

      console.log("component", i, parents[i]);

      let new_geom = new THREE.BufferGeometry();
      let vertices = [];

      for (let j = 0; j < check_geom.index.array.length; j++) {
        if (parents[check_geom.index.array[j]] == i || check_geom.index.array[j] == i) {
          let v_index = check_geom.index.array[j];
          vertices.push(check_geom.attributes.position.getX(v_index));
          vertices.push(check_geom.attributes.position.getY(v_index));
          vertices.push(check_geom.attributes.position.getZ(v_index));
        }
      }

      // next, ignore indexes from the component
      for (let j = 0; j < parents.length; j++) {
        if (j == i || parents[j] == i) {
          parents[j] = parents.length;
        }
      }

      new_geom.setAttribute('position', new THREE.Float32BufferAttribute( vertices, 3 ));
      new_geoms.push(new_geom);
    }
  }

  return new_geoms;
}